// @flow

import * as React from 'react';
import { CollectionView } from 'wijmo/wijmo';

import type { GridItem } from '../types';
import { addOrUpdateItemFromCollection } from '../utils';

export const OrderContentContext = React.createContext(
  {}, // default value
);

type Props = {
  children: React.Node,
};

type State = {
  selectedOrderType: string,
  selectedOrderDate: string,
  orderItems: CollectionView,
  hasLocalOrderItems: boolean,
  hasUnsavedOrderType: boolean,
  isLoading: boolean,
  reset: boolean,
  selectedOrderTypeFull: String
};

export const OrderContentConsumer = OrderContentContext.Consumer;

export type ChangeOrderTypeProps = {
  changes?: Array<{
    fieldName: string,
    updater?: [(item: GridItem) => mixed] | ((item: GridItem) => mixed),
    value: string,
  }>,
  Code?: string,
  currentItem?: GridItem,
  action?: '' | 'reset' | 'update',
  collection?: Array<GridItem>,
};

const defaultState = {
  selectedOrderType: '',
  selectedOrderDate: '',
  hasLocalOrderItems: false,
  hasUnsavedOrderType: false,
  isLoading: false,
  reset: false,
  selectedOrderTypeFull: ''
};

export class OrderContentProvider extends React.Component<Props, State> {
  state = {
    ...defaultState,
    orderItems: new CollectionView(),
  };

  onChangeOrderType = ({
    value,
    savedValue,
    valueFull
  }: {
    value: string,
    savedValue: string,
  }) => {
    this.setState(state => {
      return {
        selectedOrderType: value,
        hasUnsavedOrderType: value !== savedValue,
        isLoading: true,
        selectedOrderTypeFull: valueFull
      };
    });
  };

  onChangeOrderDate = (value: string) => {
    this.setState({
      selectedOrderDate: value,
    });
  };

  clearSelectedOrderType = () => {
    this.setState({
      selectedOrderType: '',
      hasUnsavedOrderType: false,
      selectedOrderTypeFull: ''
    });
  };
  clearSelectedOrderTypeFull = () => {
    this.setState({
      selectedOrderTypeFull: ''
    });
  };

  onChangeOrderItems = ({
    changes,
    Code,
    currentItem,
    action = '',
    collection,
  }: ChangeOrderTypeProps) => {
    if (changes && Code && currentItem) {
      this.setState(state => {
        // the collection `orderItems` gets mutated through the update
        // or the add method from the CollectionView class.
        const item = addOrUpdateItemFromCollection({
          collection: state.orderItems,
          changes,
          currentItem,
          Code,
          precedence: 'local',
        });

        let removedItem = {};
        if (item && item.Qty === 0) {
          removedItem = { removedItem: item.Code };
        }

        return {
          orderItems: state.orderItems,
          hasLocalOrderItems:
            state.orderItems && state.orderItems.totalItemCount > 0,
          ...removedItem,
        };
      });

      // this is triggered when the order is removed or transmited.
    } else if (action === 'reset') {
      this.setState(
        state => {
          // just to be extra sure that we won't have any cached values
          if (state.orderItems.totalItemCount) {
            state.orderItems.items.forEach(item => {
              state.orderItems.remove(item);
            });
          }

          return {
            ...defaultState,
            orderItems: new CollectionView(),
            reset: true,
          };
        },
        () => {
          this.setState({
            reset: false,
          });
        },
      );
    } else if (action === 'update') {
      this.setState({
        orderItems: new CollectionView(collection),
        isLoading: false,
      });
    }
  };

  render() {
    return (
      <OrderContentContext.Provider
        value={{
          selectedOrderType: this.state.selectedOrderType,
          onChangeOrderType: this.onChangeOrderType,
          selectedOrderDate: this.state.selectedOrderDate,
          onChangeOrderDate: this.onChangeOrderDate,
          clearSelectedOrderType: this.clearSelectedOrderType,
          clearSelectedOrderTypeFull: this.clearSelectedOrderTypeFull,
          orderItems: this.state.orderItems,
          onChangeOrderItems: this.onChangeOrderItems,
          hasLocalOrderItems: this.state.hasLocalOrderItems,
          hasUnsavedOrderType: this.state.hasUnsavedOrderType,
          isLoading: this.state.isLoading,
          resetCollection: this.state.reset,
          removedItem: this.state.removedItem,
          selectedOrderTypeFull: this.state.selectedOrderTypeFull,
        }}
      >
        {this.props.children}
      </OrderContentContext.Provider>
    );
  }
}
